import React, { useState, useEffect, useRef } from 'react';
import { reportsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import PrintButton from '../../utils/print';
import PrintLetterhead from '../common/PrintLetterhead';
import { useCompany } from '../common/useCompany';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;
const COLORS = ['#ED5D15', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2'];

const reports = [
  { key: 'profit-loss', label: 'Profit & Loss', icon: '📈' },
  { key: 'balance-sheet', label: 'Balance Sheet', icon: '⚖️' },
  { key: 'trial-balance', label: 'Trial Balance', icon: '🔢' },
  { key: 'ar-aging', label: 'AR Aging', icon: '👤' },
  { key: 'ap-aging', label: 'AP Aging', icon: '🏭' },
  { key: 'general-ledger', label: 'General Ledger', icon: '📒' },
  { key: 'journal', label: 'Journal', icon: '🗒️' },
  { key: 'sales-by-customer', label: 'Sales by Customer', icon: '📊' },
  { key: 'sales-by-product', label: 'Sales by Product', icon: '📦' },
  { key: 'invoice-list', label: 'Invoice List', icon: '🧾' },
  { key: 'purchases-by-vendor', label: 'Purchases by Vendor', icon: '🚚' },
  { key: 'cash-flow', label: 'Cash Flow', icon: '💰' },
  { key: 'expense-report', label: 'Expense Report', icon: '💸' },
  { key: 'tax-report', label: 'Tax Report', icon: '🏛️' },
  { key: 'inventory-valuation', label: 'Inventory Valuation', icon: '🏷️' },
  { key: 'customer-balances', label: 'Customer Balances', icon: '👤' },
  { key: 'vendor-balances', label: 'Vendor Balances', icon: '🏭' },
];

export default function ReportCenter() {
  const [activeReport, setActiveReport] = useState('profit-loss');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Default range spans the prior year so older records (e.g. seeded demo
  // data) show up; the user can narrow it with the date pickers.
  const [dateRange, setDateRange] = useState({
    startDate: `${new Date().getFullYear() - 1}-01-01`,
    endDate: new Date().toISOString().split('T')[0]
  });
  const reportCardRef = useRef(null);

  const loadReport = async () => {
    setLoading(true); setData(null);
    try {
      const params = { ...dateRange };
      let result;
      switch (activeReport) {
        case 'profit-loss': result = await reportsAPI.profitLoss(params); break;
        case 'balance-sheet': result = await reportsAPI.balanceSheet({ asOfDate: dateRange.endDate }); break;
        case 'trial-balance': result = await reportsAPI.trialBalance(params); break;
        case 'ar-aging': result = await reportsAPI.arAging(); break;
        case 'ap-aging': result = await reportsAPI.apAging(); break;
        case 'general-ledger': result = await reportsAPI.generalLedger(params); break;
        case 'journal': result = await reportsAPI.journal(params); break;
        case 'sales-by-customer': result = await reportsAPI.salesByCustomer(params); break;
        case 'sales-by-product': result = await reportsAPI.salesByProduct(params); break;
        case 'purchases-by-vendor': result = await reportsAPI.purchasesByVendor(params); break;
        case 'invoice-list': result = await reportsAPI.invoiceList(params); break;
        case 'cash-flow': result = await reportsAPI.cashFlow(params); break;
        case 'expense-report': result = await reportsAPI.expenseReport(params); break;
        case 'tax-report': result = await reportsAPI.taxReport(params); break;
        case 'inventory-valuation': result = await reportsAPI.inventoryValuation(); break;
        case 'customer-balances': result = await reportsAPI.customerBalances(); break;
        case 'vendor-balances': result = await reportsAPI.vendorBalances(); break;
        default: break;
      }
      setData(result);
    } catch (err) { toast.error(err.message); }
    setLoading(false);
  };

  useEffect(() => { loadReport(); }, [activeReport]);

  const renderProfitLoss = () => (
    <div>
      <div className="report-section"><h4>Revenue</h4>
        {(data.revenue?.items||[]).map((a,i)=><div key={i} className="report-row indent"><span>{a.code} - {a.name}</span><span>{fmt(a.total_balance)}</span></div>)}
        <div className="report-row subtotal"><span>Total Revenue</span><span>{fmt(data.revenue?.total)}</span></div>
      </div>
      <div className="report-section"><h4>Cost of Goods Sold</h4>
        {(data.cost_of_goods_sold?.items||[]).map((a,i)=><div key={i} className="report-row indent"><span>{a.code} - {a.name}</span><span>{fmt(Math.abs(a.total_balance))}</span></div>)}
        <div className="report-row subtotal"><span>Total COGS</span><span>{fmt(data.cost_of_goods_sold?.total)}</span></div>
      </div>
      <div className="report-row" style={{fontSize:16,fontWeight:700,borderTop:'2px solid #ED5D15',margin:'12px 0',padding:'12px 0'}}><span>Gross Profit</span><span className={data.gross_profit>=0?'text-success':'text-danger'}>{fmt(data.gross_profit)}</span></div>
      <div className="report-section"><h4>Operating Expenses</h4>
        {(data.operating_expenses?.items||[]).map((a,i)=><div key={i} className="report-row indent"><span>{a.code} - {a.name}</span><span>{fmt(Math.abs(a.total_balance))}</span></div>)}
        <div className="report-row subtotal"><span>Total Expenses</span><span>{fmt(data.operating_expenses?.total)}</span></div>
      </div>
      <div className="report-row total"><span>Net Profit / (Loss)</span><span className={data.net_profit>=0?'text-success':'text-danger'}>{fmt(data.net_profit)}</span></div>
    </div>
  );

  const renderBalanceSheet = () => (
    <div>
      <div className="grid-2">
        <div>
          <div className="report-section"><h4>Assets</h4>
            {['current','fixed','other'].map(k=>data.assets?.[k]?.items?.length>0&&(<div key={k}><h5 style={{fontSize:13,color:'#64748b',margin:'8px 0 4px'}}>{k.replace('_',' ').toUpperCase()}</h5>
              {data.assets[k].items.map((a,i)=><div key={i} className="report-row indent"><span>{a.code} - {a.name}</span><span>{fmt(a.total_balance)}</span></div>)}
              <div className="report-row subtotal"><span>{k.replace('_',' ')} Total</span><span>{fmt(data.assets[k].total)}</span></div>
            </div>))}
            <div className="report-row total"><span>Total Assets</span><span>{fmt(data.assets?.total)}</span></div>
          </div>
        </div>
        <div>
          <div className="report-section"><h4>Liabilities</h4>
            {['current','long_term'].map(k=>data.liabilities?.[k]?.items?.length>0&&(<div key={k}>
              {data.liabilities[k].items.map((a,i)=><div key={i} className="report-row indent"><span>{a.code} - {a.name}</span><span>{fmt(a.total_balance)}</span></div>)}
            </div>))}
            <div className="report-row subtotal"><span>Total Liabilities</span><span>{fmt(data.liabilities?.total)}</span></div>
          </div>
          <div className="report-section"><h4>Equity</h4>
            {(data.equity?.items||[]).map((a,i)=><div key={i} className="report-row indent"><span>{a.code} - {a.name}</span><span>{fmt(a.total_balance)}</span></div>)}
            <div className="report-row subtotal"><span>Total Equity</span><span>{fmt(data.equity?.total)}</span></div>
          </div>
          <div className="report-row total"><span>Total L & E</span><span>{fmt(data.total_liabilities_and_equity)}</span></div>
          {data.is_balanced ? <div className="text-success" style={{textAlign:'center',marginTop:8}}>✅ Balance Sheet is balanced</div> : <div className="text-danger" style={{textAlign:'center',marginTop:8}}>⚠️ Balance Sheet is unbalanced</div>}
        </div>
      </div>
    </div>
  );

  const renderGenericTable = (items, columns) => {
    const rows = Array.isArray(items) ? items : [];
    return (
      <table><thead><tr>{columns.map((c,i)=><th key={i} className={c.align||''}>{c.label}</th>)}</tr></thead>
        <tbody>{rows.map((row,i)=>(<tr key={i}>{columns.map((c,j)=><td key={j} className={c.cellClass||''}>{c.render?c.render(row):row[c.key]}</td>)}</tr>))}
          {rows.length===0&&<tr><td colSpan={columns.length} className="text-center text-muted" style={{padding:30}}>No data for this period</td></tr>}
        </tbody></table>
    );
  };

  const renderContent = () => {
    if (loading) return <div className="spinner" style={{margin:'40px auto'}} />;
    if (!data) return null;

    switch(activeReport) {
      case 'profit-loss': return renderProfitLoss();
      case 'balance-sheet': return renderBalanceSheet();
      case 'trial-balance':
        return renderGenericTable(data.accounts, [
          {key:'code',label:'Code',render:r=><span className="font-mono">{r.code}</span>},
          {key:'name',label:'Account'},
          {key:'type_name',label:'Type',render:r=><span className="badge badge-draft">{r.type_name}</span>},
          {key:'total_debit',label:'Debit',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_debit)},
          {key:'total_credit',label:'Credit',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_credit)},
          {key:'balance',label:'Balance',align:'text-right',cellClass:'amount',render:r=><strong>{fmt(r.balance)}</strong>},
        ]);
      case 'ar-aging':
        return <div>{renderGenericTable(data.invoices,[
          {key:'customer_name',label:'Customer'},{key:'invoice_number',label:'Invoice #'},
          {key:'due_date',label:'Due Date'},{key:'amount_due',label:'Amount Due',align:'text-right',cellClass:'amount',render:r=>fmt(r.amount_due)},
          {key:'aging_bucket',label:'Aging',render:r=><span className="badge badge-draft">{r.aging_bucket}</span>},
        ])}<div style={{marginTop:16}}><h4>Aging Summary</h4><div className="stats-grid">{Object.entries(data.summary||{}).map(([k,v])=><div key={k} className="stat-card"><div className="stat-label">{k.replace(/_/g,' ')}</div><div className="stat-value">{fmt(v)}</div></div>)}</div></div></div>;
      case 'ap-aging':
        return renderGenericTable(data.bills,[
          {key:'vendor_name',label:'Vendor'},{key:'bill_number',label:'Bill #'},
          {key:'due_date',label:'Due Date'},{key:'amount_due',label:'Amount Due',align:'text-right',cellClass:'amount',render:r=>fmt(r.amount_due)},
          {key:'aging_bucket',label:'Aging',render:r=><span className="badge badge-draft">{r.aging_bucket}</span>},
        ]);
      case 'journal':
        return renderGenericTable(data,[
          {key:'entry_date',label:'Date'},{key:'entry_number',label:'Entry #',render:r=><span className="font-mono">{r.entry_number}</span>},
          {key:'account_code',label:'Account',render:r=><span className="font-mono">{r.account_code}</span>},
          {key:'account_name',label:'Account Name'},
          {key:'description',label:'Description'},
          {key:'debit',label:'Debit',align:'text-right',cellClass:'amount',render:r=>r.debit>0?fmt(r.debit):''},
          {key:'credit',label:'Credit',align:'text-right',cellClass:'amount',render:r=>r.credit>0?fmt(r.credit):''},
        ]);
      case 'sales-by-customer':
        return renderGenericTable(data,[
          {key:'name',label:'Customer'},{key:'invoice_count',label:'Invoices'},
          {key:'total_sales',label:'Total Sales',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_sales)},
          {key:'outstanding',label:'Outstanding',align:'text-right',cellClass:'amount',render:r=>fmt(r.outstanding)},
        ]);
      case 'sales-by-product':
        return renderGenericTable(data,[
          {key:'name',label:'Product'},{key:'sku',label:'SKU'},
          {key:'quantity_sold',label:'Qty Sold',align:'text-right'},
          {key:'total_sales',label:'Total Sales',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_sales)},
        ]);
      case 'purchases-by-vendor':
        return renderGenericTable(data,[
          {key:'name',label:'Vendor'},{key:'bill_count',label:'Bills'},
          {key:'total_purchases',label:'Total Purchases',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_purchases)},
          {key:'outstanding',label:'Outstanding',align:'text-right',cellClass:'amount',render:r=>fmt(r.outstanding)},
        ]);
      case 'invoice-list':
        return <div>{renderGenericTable(data.invoices,[
          {key:'invoice_number',label:'Invoice #',render:r=><span className="font-mono">{r.invoice_number}</span>},
          {key:'invoice_date',label:'Date'},
          {key:'customer_name',label:'Customer'},
          {key:'total',label:'Total',align:'text-right',cellClass:'amount',render:r=>fmt(r.total)},
          {key:'amount_due',label:'Balance Due',align:'text-right',cellClass:'amount',render:r=><strong className={r.amount_due>0?'text-danger':''}>{fmt(r.amount_due)}</strong>},
          {key:'status',label:'Status',render:r=><span className={`badge badge-${r.status?.toLowerCase()}`}>{r.status}</span>},
        ])}
        <div className="stats-grid">
          <div className="stat-card"><div className="stat-label">Invoices</div><div className="stat-value">{data.count||0}</div></div>
          <div className="stat-card"><div className="stat-label">Total</div><div className="stat-value">{fmt(data.total)}</div></div>
          <div className="stat-card"><div className="stat-label">Total Due</div><div className="stat-value">{fmt(data.total_due)}</div></div>
        </div></div>;
      case 'cash-flow':
        return <div>
          <div className="report-section"><h4>Operating Activities</h4>
            <div className="report-row indent"><span>Customer Receipts</span><span className="text-success">{fmt(data.operating?.customer_receipts)}</span></div>
            <div className="report-row indent"><span>Vendor Payments</span><span className="text-danger">({fmt(data.operating?.vendor_payments)})</span></div>
            <div className="report-row indent"><span>Expenses</span><span className="text-danger">({fmt(data.operating?.expenses)})</span></div>
            <div className="report-row subtotal"><span>Net Operating</span><span>{fmt(data.operating?.net)}</span></div>
          </div>
          <div className="report-section"><h4>Investing Activities</h4>
            <div className="report-row indent"><span>Asset Purchases</span><span className="text-danger">({fmt(data.investing?.asset_purchases)})</span></div>
            <div className="report-row subtotal"><span>Net Investing</span><span>{fmt(data.investing?.net)}</span></div>
          </div>
          <div className="report-row total"><span>Net Change in Cash</span><span className={data.net_change>=0?'text-success':'text-danger'}>{fmt(data.net_change)}</span></div>
        </div>;
      case 'general-ledger':
        return renderGenericTable(data,[
          {key:'posting_date',label:'Date'},{key:'entry_number',label:'Entry #',render:r=><span className="font-mono">{r.entry_number}</span>},
          {key:'account_code',label:'Account',render:r=><span className="font-mono">{r.account_code}</span>},
          {key:'description',label:'Description'},{key:'debit',label:'Debit',align:'text-right',cellClass:'amount',render:r=>r.debit>0?fmt(r.debit):''},
          {key:'credit',label:'Credit',align:'text-right',cellClass:'amount',render:r=>r.credit>0?fmt(r.credit):''},
        ]);
      case 'expense-report':
        return <div>{renderGenericTable(data.expenses,[
          {key:'expense_number',label:'#'},{key:'expense_date',label:'Date'},{key:'category_name',label:'Category'},
          {key:'vendor_name',label:'Vendor'},{key:'description',label:'Description'},
          {key:'total',label:'Amount',align:'text-right',cellClass:'amount',render:r=>fmt(r.total)},
        ])}<div className="report-row total"><span>Total Expenses</span><span>{fmt(data.total)}</span></div></div>;
      case 'inventory-valuation':
        return <div>{renderGenericTable(data.items,[
          {key:'sku',label:'SKU',render:r=><span className="font-mono">{r.sku}</span>},{key:'name',label:'Name'},
          {key:'quantity_on_hand',label:'Qty On Hand',align:'text-right'},{key:'average_cost',label:'Avg Cost',align:'text-right',cellClass:'amount',render:r=>fmt(r.average_cost)},
          {key:'total_value',label:'Value',align:'text-right',cellClass:'amount',render:r=><strong>{fmt(r.total_value)}</strong>},
        ])}<div className="report-row total"><span>Total Inventory Value</span><span>{fmt(data.total_value)}</span></div></div>;
      case 'customer-balances':
        return renderGenericTable(data.customers,[
          {key:'customer_number',label:'#',render:r=><span className="font-mono">{r.customer_number}</span>},{key:'name',label:'Customer'},
          {key:'email',label:'Email'},{key:'balance',label:'Balance',align:'text-right',cellClass:'amount',render:r=><strong className={r.balance>0?'text-danger':''}>{fmt(r.balance)}</strong>},
        ]);
      case 'vendor-balances':
        return renderGenericTable(data.vendors,[
          {key:'vendor_number',label:'#',render:r=><span className="font-mono">{r.vendor_number}</span>},{key:'name',label:'Vendor'},
          {key:'email',label:'Email'},{key:'balance',label:'Balance',align:'text-right',cellClass:'amount',render:r=><strong className={r.balance>0?'text-danger':''}>{fmt(r.balance)}</strong>},
        ]);
      case 'tax-report':
        return <div>
          <div className="report-section"><h4>Sales Tax Collected</h4>{renderGenericTable(data.sales_tax,[{key:'tax_name',label:'Tax'},{key:'rate',label:'Rate'},{key:'taxable_amount',label:'Taxable',align:'text-right',cellClass:'amount',render:r=>fmt(r.taxable_amount)},{key:'total_tax',label:'Tax',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_tax)}])}</div>
          <div className="report-section"><h4>Purchase Tax Paid</h4>{renderGenericTable(data.purchase_tax,[{key:'tax_name',label:'Tax'},{key:'rate',label:'Rate'},{key:'taxable_amount',label:'Taxable',align:'text-right',cellClass:'amount',render:r=>fmt(r.taxable_amount)},{key:'total_tax',label:'Tax',align:'text-right',cellClass:'amount',render:r=>fmt(r.total_tax)}])}</div>
          <div className="report-row total"><span>Net Tax Liability</span><span>{fmt(data.net_tax_liability)}</span></div>
        </div>;
      default: return <p>Select a report</p>;
    }
  };

  const activeLabel = reports.find(r=>r.key===activeReport)?.label || 'Report';
  const company = useCompany();

  return (
    <div>
      <div className="toolbar"><h2>Reports Center</h2></div>
      <div className="rep-layout">
        <div className="card rep-sidebar">
          <div className="rep-sidebar-title">Reports</div>
          <div className="rep-list">
            {reports.map(r => (
              <button key={r.key} className={`rep-list-btn ${activeReport===r.key?'active':''}`} onClick={() => { setActiveReport(r.key); setData(null); }}>
                <span className="rep-list-icon">{r.icon}</span>{r.label}
              </button>
            ))}
          </div>
        </div>
        <div>
          <div className="card" style={{marginBottom:16}}><div className="card-body rep-controls">
            <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
              <label style={{fontSize:12,fontWeight:500}}>From:</label>
              <input type="date" className="form-control" value={dateRange.startDate} onChange={e=>setDateRange({...dateRange,startDate:e.target.value})} style={{width:150}}/>
              <label style={{fontSize:12,fontWeight:500}}>To:</label>
              <input type="date" className="form-control" value={dateRange.endDate} onChange={e=>setDateRange({...dateRange,endDate:e.target.value})} style={{width:150}}/>
              <button className="btn btn-primary btn-sm" onClick={loadReport}>Generate</button>
              <PrintButton targetRef={reportCardRef} title={`${activeLabel} - ${new Date().toLocaleDateString()}`} className="btn btn-outline btn-sm" />
            </div>
          </div></div>
          <div className="card"><div className="card-body rep-report-body" ref={reportCardRef}>
            <PrintLetterhead />
            <h3 style={{marginBottom:16}}>{activeLabel}</h3>
            {renderContent()}
            <div className="rep-report-footer">
              <div className="rep-report-footer-line">
                {[
                  company && company.name,
                  company && company.phone && `Phone: ${company.phone}`,
                  company && company.email,
                  company && company.website,
                ].filter(Boolean).join('  ·  ')}
              </div>
            </div>
          </div></div>
        </div>
      </div>
    </div>
  );
}
